import MDBox from "components/MDBox";
import { useEffect, useState, useRef, useMemo } from "react";
import {
  Typography,
  Paper,
  Button,
  Box,
  Fab,
  CircularProgress,
  Stack,
  Chip,
  IconButton,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/Work";
import LoadingScreen from "components/Loading";
import { useTranslation } from "react-i18next";
import SnackbarWrapper from "components/Snackbar";
import { useLocation } from "react-router-dom";
import AddSuccessorDialog from "components/AddSuccessorDialog";
import Icon from "@mui/material/Icon";
import { addEmployeeSuccessor, getSuccessorsWithStepsByJobId, deleteSuccessor } from "services/api";
import { useNavigate } from "react-router-dom";
import EmployeeAvatar from "components/EmployeeAvatar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SuccessionStepper from "./Steps";
import SuccessorTrainingDialog from "components/SuccessorTrainingDialog";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ConfirmationDialog from "components/ConfirmationDialog";

const Successors = () => {
  const { t } = useTranslation();
  const snackbarRef = useRef();
  const { state } = useLocation();
  const { job, employees, jobs } = state || {};
  const [successors, setSuccessors] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [loadingSaveSucc, setLoadingSaveSucc] = useState(false);
  const [activeTrainingsMap, setActiveTrainingsMap] = useState({});
  // Boolean for the Dialog visibility
  const [isStepTrainingDialogOpen, setIsStepTrainingDialogOpen] = useState(false);

  // Object containing the specific trainings for the clicked step
  const [selectedStepTrainingDetails, setSelectedStepTrainingDetails] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteSuccessorId, setDeleteSuccessorId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchSuccessors = async () => {
    if (!job?.jobId) return;
    setLoading(true);

    try {
      const data = await getSuccessorsWithStepsByJobId(job.jobId); //get successors for this job

      if (data !== "error") {
        const today = new Date();
        const tempInfoMap = {};

        // 1. Get the IDs that have a business rank
        const rankedIds = (job.successors || []).map((s) => s.successorId);
        // 2. Build the Ranked List
        // We look inside the API 'data' and find the FULL OBJECT for each ranked ID
        const rankedList = (job.successors || [])
          .map((rankEntry) => {
            // This line pulls the full detailed object FROM the 'data' variable
            return data.find((item) => item.id === rankEntry.successorId);
          })
          .filter(Boolean); // Clean up if an ID in rank doesn't exist in API data
        // 3. Build the Unranked List
        // We take everyone else FROM the 'data' variable who is NOT in the ranked list
        const unrankedList = data.filter((item) => !rankedIds.includes(item.id));
        // 4. Final Ordered List: Ranked details first, then the remaining details
        const finalOrderedList = [...rankedList, ...unrankedList];

        // data.forEach((successor) => {
        finalOrderedList.forEach((successor) => {
          // Sort steps chronologically
          const sortedSteps = [...(successor.steps || [])].sort(
            (a, b) => new Date(a.planDate) - new Date(b.planDate)
          );
          successor.steps = sortedSteps;

          const activeIndex = sortedSteps.findIndex((s) => new Date(s.planDate) >= today);
          const currentStep = sortedSteps[activeIndex];

          if (currentStep) {
            tempInfoMap[successor.id] = {
              stepLabel: `Step ${activeIndex + 1}`,
              trainings: currentStep.trainings.filter((t) => !t.finished),
            };
          }
        });

        setSuccessors(finalOrderedList);
        setActiveTrainingsMap(tempInfoMap);
      } else {
        setSuccessors([]);
        setActiveTrainingsMap({});
      }
    } catch (error) {
      console.error("Error fetching successors:", error);
      setSuccessors([]);
    } finally {
      setLoading(false); // 👈 STOP loading
    }
  };

  useEffect(() => {
    fetchSuccessors();
  }, [job?.jobId]);

  const handleSaveSuccessor = async (successorEmployeeId, successorJobId) => {
    setLoadingSaveSucc(true);
    try {
      const payload = {
        employeeId: successorEmployeeId,
        successorJobId: successorJobId,
      };

      const result = await addEmployeeSuccessor(payload);

      if (result !== "error") {
        snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.successorAdded"), "success");
        await fetchSuccessors();
        setIsDialogOpen(false);
      } else {
        snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.successorFailed"), "error");
      }
    } catch {
      snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.successorFailed"), "error");
    } finally {
      setLoadingSaveSucc(false);
    }
  };

  const handleOpenSuccessorConfirm = (id) => {
    setDeleteSuccessorId(id);
    setConfirmOpen(true);
  };

  const handleDeleteSuccessor = async (id) => {
    setDeleteLoading(true);

    try {
      const result = await deleteSuccessor(id);

      if (result !== "error") {
        setSuccessors((prev) => prev.filter((s) => s.id !== id));

        snackbarRef.current?.showSnackbar(
          t("admin.successionPlanSteps.successorDeleted"),
          "success"
        );
      } else {
        snackbarRef.current?.showSnackbar(
          t("admin.successionPlanSteps.successorDeletedFailed"),
          "error"
        );
      }
    } catch (error) {
      console.error("Error deleting successor:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleBack = () => {
    navigate(-1); // goes to previous page
  };

  const handleSuccessorClick = (person) => {
    if (deleteLoading) return;
    navigate("/succession-steps", {
      state: {
        successor: person, // successor data
        job, //selected job to see its succession plan
        jobs, // all jobs
      },
    });
  };

  const handleViewStepTrainingDetails = (event, trainingInfo, successorName) => {
    event.stopPropagation(); // Prevent card click navigation
    setSelectedStepTrainingDetails({
      ...trainingInfo,
      successorName,
    });
    setIsStepTrainingDialogOpen(true);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ p: 3, margin: "auto" }}>
      <Button
        onClick={handleBack}
        startIcon={<ArrowBackIcon />}
        sx={{
          mb: 1,
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        {t("common.back")}
      </Button>
      {/* Header Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight="bold" color="text.primary" gutterBottom>
          {job?.jobName}
          <WorkIcon fontSize="medium" color="info" sx={{ ml: 1, verticalAlign: "middle" }} />
        </Typography>
      </Box>

      <Typography variant="subtitle2" sx={{ mb: 2, color: "text.secondary" }}>
        {successors.length} {t("admin.successionPlanSteps.successors")}
      </Typography>

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {/* {successors.map((person, index) => ( */}
          {successors.map((person, index) => {
            const activeInfo = activeTrainingsMap[person?.id];
            const rankIndex = job.successors?.findIndex((s) => s.successorId === person.id);
            // If found (index 0, 1, 2...), show #Rank. Otherwise, just "Successor"
            const chipLabel =
              rankIndex !== -1
                ? `${t("admin.successionPlanSteps.successor")} #${rankIndex + 1}`
                : t("admin.successionPlanSteps.successors").slice(0, -1); // Or just "Successor"
            return (
              <Paper
                key={person.id}
                onClick={() => handleSuccessorClick(person)}
                elevation={0}
                sx={{
                  p: 2,
                  pb: 2,
                  borderRadius: "4px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    width: "100%",
                    overflow: "hidden",
                    gap: 2,

                    // Mobile layout
                    flexDirection: {
                      xs: "column",
                      md: "row",
                    },
                  }}
                >
                  {/* 1. PHOTO SECTION */}
                  <EmployeeAvatar
                    photo={person.photo}
                    sx={{
                      width: 80,
                      height: 80,
                      mr: 3,
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                      border: "2px solid #fff",
                      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                    }}
                  />

                  {/* 2. EMPLOYEE INFO SECTION */}
                  <Box
                    // sx={{ flexGrow: 1 }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",

                      // Desktop/tablet behavior
                      flex: {
                        xs: "unset",
                        md: "0 1 250px",
                      },

                      minWidth: {
                        md: 180,
                      },

                      maxWidth: {
                        md: 300,
                      },

                      width: {
                        xs: "100%",
                        md: "auto",
                      },
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      {person.employeeName}
                    </Typography>

                    {/* PIPELINE RANK BADGE */}
                    <Chip
                      label={chipLabel}
                      size="small"
                      sx={{
                        bgcolor: rankIndex !== -1 ? "#e3f2fd" : "#f5f5f5",
                        color: rankIndex !== -1 ? "#1976d2" : "#757575",
                        fontWeight: "bold",
                        borderRadius: "6px",
                        fontSize: "0.7rem",
                        border: "1px solid #bbdefb",
                      }}
                    />
                    {activeInfo && activeInfo.trainings.length > 0 && (
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<Icon>school</Icon>}
                        onClick={(e) =>
                          handleViewStepTrainingDetails(e, activeInfo, person.employeeName)
                        }
                        sx={{
                          mt: 1,
                          textTransform: "none",
                          fontWeight: "bold",
                          color: "#1976d2",
                          "&:hover": { bgcolor: "#f0f7ff" },
                        }}
                      >
                        {activeInfo.stepLabel} {t("admin.successionPlanSteps.prog")}
                      </Button>
                    )}

                    <IconButton
                      size="small"
                      disabled={deleteLoading}
                      // onClick={() => handleOpenSuccessorConfirm(person.id)}
                      onClick={(e) => {
                        e.stopPropagation(); // 👈 IMPORTANT
                        handleOpenSuccessorConfirm(person.id);
                      }}
                      sx={{
                        border: "1px solid #eee",
                        borderRadius: "8px",
                        color: "#ef4444",
                        mt: 2,
                      }}
                    >
                      {deleteLoading ? (
                        <CircularProgress size={16} sx={{ color: "black" }} />
                      ) : (
                        <DeleteOutlineIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  {/* 3. STEPPER PLACEHOLDER (To match UI layout) */}
                  {/* 3. STEPPER SECTION */}
                  <Box
                    sx={{
                      flex: "1 1 auto", // takes remaining space dynamically
                      minWidth: 0,
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    {person.steps && person.steps.length > 0 && (
                      <Box
                        sx={{
                          width: "100%",
                          minWidth: 0,
                        }}
                      >
                        <SuccessionStepper steps={person.steps} />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
          <MDBox sx={{ position: "fixed", bottom: 20, right: 100 }}>
            <Fab color="primary" aria-label="add" onClick={() => handleOpenDialog(null)}>
              <Icon>add</Icon>
            </Fab>
          </MDBox>
          <SuccessorTrainingDialog
            open={isStepTrainingDialogOpen}
            onClose={() => setIsStepTrainingDialogOpen(false)}
            data={selectedStepTrainingDetails}
            employeeName={selectedStepTrainingDetails?.successorName}
          />
          <AddSuccessorDialog
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            allEmployees={employees} // Passed from location.state
            allJobs={jobs} // Passed from location.state
            currentJob={job} // The specific job user clicked on
            onSave={handleSaveSuccessor}
            loading={loadingSaveSucc}
            successorEmployees={successors}
          />
          <ConfirmationDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={async () => {
              setConfirmOpen(false);

              if (deleteSuccessorId) {
                await handleDeleteSuccessor(deleteSuccessorId);
                setDeleteSuccessorId(null);
              }
            }}
          />
          <SnackbarWrapper
            ref={snackbarRef}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          />
        </Stack>
      )}
    </Box>
  );
};

export default Successors;
